import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { catalogApi } from './catalogApi';

/**
 * Backs the "pick a catalog item" comboboxes on the requisition, market list, purchase order and
 * recipe forms.
 *
 * These forms used to load one big page of items and filter it in the browser, so any catalog
 * larger than that page had items nobody could pick — they simply never arrived, and the combobox
 * said "no catalog matches" for products that plainly existed. Typing now queries the server
 * (which also matches SKU and barcode, unlike the old local name-only filter) and results are
 * merged into a pool that keeps everything already seen, so lines that reference an item stay
 * resolvable after the query changes.
 */
const BROWSE_PAGE_SIZE = 100;
const SEARCH_PAGE_SIZE = 50;
const DEBOUNCE_MS = 250;

/**
 * @param status Item status to restrict to, or null for every status. Procurement documents only
 *   order active items; the supplier price list can reference any of them.
 */
export function useCatalogItemPicker({ propertyId, enabled = true, status = 'Active' } = {}) {
  const [pool, setPool] = useState([]);
  const [searching, setSearching] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);

  /** Union by id — never drop an item a line might still point at. */
  const merge = useCallback((incoming) => {
    if (!incoming?.length) return;
    setPool((prev) => {
      const byId = new Map(prev.map((i) => [i.id, i]));
      for (const item of incoming) byId.set(item.id, item);
      return [...byId.values()];
    });
  }, []);

  // First page for instant browsing before the user types anything.
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    catalogApi
      .listItems({ pageSize: BROWSE_PAGE_SIZE, sortBy: 'name', ...(status ? { status } : {}) })
      .then((res) => {
        if (!active) return;
        merge(res?.items || []);
        setTotalCount(res?.totalCount ?? null);
      })
      .catch(() => {});
    return () => { active = false; };
    // propertyId changes swap the tenant scope, so the pool is refetched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, enabled, status, merge]);

  const runSearch = useCallback(async (term) => {
    const seq = ++requestSeqRef.current;
    setSearching(true);
    try {
      const res = await catalogApi.listItems({
        search: term,
        pageSize: SEARCH_PAGE_SIZE,
        sortBy: 'name',
        ...(status ? { status } : {}),
      });
      if (seq !== requestSeqRef.current) return; // a newer keystroke already won
      merge(res?.items || []);
    } catch {
      /* keep whatever is already in the pool */
    } finally {
      if (seq === requestSeqRef.current) setSearching(false);
    }
  }, [merge, status]);

  /**
   * Loads matches for what the user is typing. Deliberately does not filter `options`: a form has
   * one picker per line but they all share this pool, so filtering here would let a query typed on
   * one line narrow another line's list. Each combobox filters the pool by its own query instead.
   */
  const onSearchChange = useCallback((next) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = (next || '').trim();
    if (!term) { setSearching(false); return; }
    debounceRef.current = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  }, [runSearch]);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const options = useMemo(
    () => pool
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((i) => ({ value: i.id, label: i.name, description: i.sku || undefined })),
    [pool],
  );

  /** Adds items a document already references (e.g. when editing) so their names resolve. */
  const ensureItems = useCallback((items) => merge(items), [merge]);

  /**
   * Pulls in items referenced by an existing document that the browse page never returned, so an
   * edited line shows its item name instead of an empty picker.
   */
  const requestedRef = useRef(new Set());
  const ensureIds = useCallback(async (ids) => {
    const missing = (ids || []).filter(
      (id) => id && !requestedRef.current.has(id) && !pool.some((i) => i.id === id),
    );
    if (!missing.length) return;
    missing.forEach((id) => requestedRef.current.add(id));
    const fetched = await Promise.all(missing.map((id) => catalogApi.getItem(id).catch(() => null)));
    merge(fetched.filter(Boolean));
  }, [pool, merge]);

  const findItem = useCallback((id) => pool.find((i) => i.id === id), [pool]);

  return {
    items: pool, options, onSearchChange, searching, totalCount,
    ensureItems, ensureIds, findItem,
  };
}
